import React from 'react';
import {mountInvisible} from '@stripe-internal/stripethirdparty';
import type {InvisibleConfig} from '@stripe-internal/stripethirdparty/lib/shared/types';
import {throttle} from 'lodash-es';
import type {AddressFieldValue} from '.';
import {useFlowConfigurationContext} from '../../FlowConfigurationContext';

type Props = {
  useMapboxAutocomplete: boolean;
  children: (addressAutocomplete: AddressAutocompleteFunction) => JSX.Element;
};
const mountAutoCompete = (config: InvisibleConfig<'AddressAutocomplete'>) =>
  mountInvisible<'AddressAutocomplete'>(config);

export type ProxyAPI = ReturnType<typeof mountAutoCompete>;
type AddressGuess = {
  name: string;
  address: Omit<AddressFieldValue, 'country'>;
};
type MapBoxContext = {
  id: string;
  short_code?: string;
  text: string;
};

type MapBoxFeature = {
  address?: string;
  text: string;
  place_name: string;
  place_type: Array<string>;
  context: Array<MapBoxContext>;
};

const initializeMapboxToAddressMapping = (): Record<
  string,
  {
    key: keyof AddressFieldValue;
    rank?: number | undefined;
    transform?: (context: MapBoxContext) => string | undefined;
  }
> => {
  return {
    // This is city
    place: {key: 'city', rank: 2},
    // This is state/province
    region: {
      key: 'state',
      transform: (context: MapBoxContext) => {
        if (context.short_code) {
          return context.short_code.toUpperCase().split('-').pop();
        }
        return context.text;
      },
    },
    // TODO: I'm not sure this is right...
    locality: {key: 'city', rank: 1},
    postcode: {key: 'postal_code'},
    address: {key: 'line1'},
    'address-line2': {key: 'line2'},
  };
};

const mapbox_to_address_mapping = initializeMapboxToAddressMapping();

const updateRank = (context: MapBoxContext, newRank: number) => {
  const type = context.id.split('.')[0];
  const mapping = mapbox_to_address_mapping[type];
  if (mapping) {
    mapping.rank = newRank;
  }
};

const guessAddress = (feature: MapBoxFeature): string => {
  // MapBox unfortunately doesn't provide us with just address line1.
  // It's either split between text and address or within the full place_name.
  // We'll try to guess it by seeing if our guesses are in place_name...
  const {place_name: placeName, address = '', text} = feature;

  // Let's see if trailing address fits...
  const trailingAddress = [text, address].filter(Boolean).join(' ');
  if (placeName.indexOf(trailingAddress) === 0) {
    return trailingAddress;
  }

  // Otherwise, fall back to address first
  return [address, text].filter(Boolean).join(' ');
};

export const mapboxToAddress = (feature: MapBoxFeature): AddressGuess => {
  const initial = {
    line1: {text: guessAddress(feature)},
    line2: {text: ''},
    postal_code: {text: ''},
    state: {text: ''},
  } as const;

  // If there's both a locality and a place, we want to utilize the one that matches the place_name
  // (which is what the user selects in the in the dropdown text)
  const placeContext = feature.context.find((context) =>
    context.id.startsWith('place'),
  );
  const localityContext = feature.context.find((context) =>
    context.id.startsWith('locality'),
  );

  let rankingsChanged = false;
  if (placeContext && localityContext) {
    rankingsChanged = true;
    // Set a higher rank for the one that matches the place_name text
    if (feature.place_name.includes(localityContext.text)) {
      updateRank(localityContext, 1);
      updateRank(placeContext, 2);
    } else if (feature.place_name.includes(placeContext.text)) {
      updateRank(placeContext, 1);
      updateRank(localityContext, 2);
    }
  }

  const addressMappingWithRank = feature.context.reduce((accum, context) => {
    const {id, text} = context;
    const type = id.split('.')[0];
    const mapping = mapbox_to_address_mapping[type];

    if (mapping) {
      const valuePair = {
        text: mapping.transform ? mapping.transform(context) : text,
        rank: mapping.rank,
      };
      // if we already have a value for this key and the rank is worse (i.e. greater numerically)
      // than the current value, don't update it
      if (
        // @ts-expect-error todo: fix implicit any
        accum[mapping.key] &&
        valuePair.rank &&
        // @ts-expect-error todo: fix implicit any
        accum[mapping.key].rank &&
        // @ts-expect-error todo: fix implicit any
        accum[mapping.key].rank < valuePair.rank
      ) {
        return accum;
      }

      return {
        ...accum,
        [mapping.key]: valuePair,
      };
    }
    return accum;
  }, initial);

  // Drop the rank from the object
  const addressMapping = {};
  Object.keys(addressMappingWithRank).forEach((key) => {
    // @ts-expect-error todo: fix implicit any
    addressMapping[key] = addressMappingWithRank[key].text;
  });

  if (rankingsChanged) {
    initializeMapboxToAddressMapping();
  }

  return {
    name: feature.place_name,
    address: addressMapping,
  };
};

const getAutocompleteFunction = (mapboxIframe: {
  current: ProxyAPI | null | undefined;
}) => {
  const {current: iframe} = mapboxIframe;
  if (!iframe) {
    return () => Promise.resolve<AddressGuess[]>([]);
  }

  return throttle(
    async (
      query: string,
      country: string,
      proximity?: {
        latitude?: number;
        longitude?: number;
      },
    ) => {
      return (
        await iframe.sendRequest({
          query,
          country,
          proximity,
        })
      ).features.map(mapboxToAddress);
    },
    500,
    {leading: true, trailing: true},
  );
};

const noop = async () => [];

export type AddressAutocompleteFunction = (
  query: string,
  country: string,
  proximity?: {
    latitude?: number;
    longitude?: number;
  },
) => Promise<Array<AddressGuess>> | undefined;

export const AddressAutocompleteIframe: React.ComponentType<Props> = (
  props,
) => {
  const {stripeThirdPartyBaseUrl: baseURL} = useFlowConfigurationContext();
  const mapboxIframe = React.useRef<ProxyAPI>();
  React.useEffect(() => {
    mapboxIframe.current =
      props.useMapboxAutocomplete && baseURL
        ? mountAutoCompete({
            type: 'AddressAutocomplete',
            baseURL,
          })
        : undefined;
    return mapboxIframe.current?.unmount;
  }, [baseURL, props.useMapboxAutocomplete]);
  const autocomplete = React.useMemo(
    () => getAutocompleteFunction(mapboxIframe),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapboxIframe, mapboxIframe.current],
  );
  return props.useMapboxAutocomplete
    ? props.children(autocomplete)
    : props.children(noop);
};
